import React from 'react'
import ReactDOM from 'react-dom'
import { Button, ButtonIcon } from '@arvoreeducacao/bonsai-ds'
import { useTranslation } from 'i18n'
import { useAddToList } from './ButtonAddToList.hooks'
import { ButtonList, ContentButton } from './ButtonAddToList.styles'
import type { ButtonAddToListProps } from './ButtonAddToList.types'

const ButtonAddToList: React.FC<ButtonAddToListProps> = ({
  slug,
  children,
  onModalClose
}) => {
  const { t } = useTranslation()
  const {
    isInAnyList,
    isModalOpen,
    lists,
    checkedIds,
    isLoading,
    isCreating,
    showCreateForm,
    newListName,
    setNewListName,
    setShowCreateForm,
    openModal,
    closeModal,
    toggleList,
    createList
  } = useAddToList({ slug })

  const booksCountLabel = (count: number) => {
    if (count === 0) return t('vazia')
    if (count === 1) return t('1 livro')
    return t('{{count}} livros', { count })
  }

  const handleCloseModal = () => {
    closeModal()
    onModalClose?.()
  }

  const portalContainer = document.getElementById('modal') || document.body

  return (
    <>
      {children && React.isValidElement(children) ? (
        React.cloneElement(children as React.ReactElement, {
          onClick: (event: React.MouseEvent) => {
            ;(children as React.ReactElement).props.onClick?.(event)
            openModal()
          }
        })
      ) : children ? (
        <span onClick={openModal}>{children}</span>
      ) : (
        <ContentButton onClick={openModal}>
          <ButtonList
            iconName={isInAnyList ? 'list-check' : 'list'}
            isInList={isInAnyList}
            theme={{ type: 'secondary', size: 'xLarge' }}
            aria-label={t('Adicionar à lista')}
          />
        </ContentButton>
      )}

      {isModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/50 lg:items-center">
            <div className="flex w-full flex-col gap-4 rounded-t-2xl bg-white p-6 lg:w-[420px] lg:rounded-2xl lg:p-8">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-zinc-900">
                  {t('Adicionar à lista')}
                </h2>
                <ButtonIcon
                  iconName="cancel"
                  theme={{ type: 'ghost', size: 'medium' }}
                  onClick={handleCloseModal}
                  aria-label={t('Fechar')}
                />
              </div>

              {isLoading ? (
                <p className="py-6 text-center text-sm text-zinc-600">
                  {t('Carregando...')}
                </p>
              ) : (
                <>
                  {lists.length === 0 && !showCreateForm && (
                    <p className="py-4 text-center text-sm text-zinc-600">
                      {t('Você ainda não tem listas. Crie a primeira!')}
                    </p>
                  )}

                  {lists.length > 0 && (
                    <div className="flex max-h-[280px] flex-col gap-1 overflow-y-auto">
                      {lists.map(list => (
                        <label
                          className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-zinc-100"
                          key={list.id}
                        >
                          <input
                            checked={checkedIds.includes(list.id)}
                            className="h-4 w-4 cursor-pointer accent-emerald-600"
                            onChange={() => toggleList(list.id)}
                            type="checkbox"
                          />
                          <span className="flex-1 truncate text-[15px] font-medium text-zinc-900">
                            {list.name}
                          </span>
                          <span className="shrink-0 text-xs text-zinc-500">
                            {booksCountLabel(list.itemsCount)}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {showCreateForm ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        className="h-10 flex-1 rounded-lg border border-zinc-300 px-3 text-sm outline-none focus:border-zinc-500"
                        maxLength={255}
                        onChange={event => setNewListName(event.target.value)}
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            void createList()
                          }
                        }}
                        placeholder={t('Nome da lista')}
                        value={newListName}
                      />
                      <Button
                        disabled={!newListName.trim() || isCreating}
                        onClick={() => void createList()}
                        theme={{ type: 'primaryLivros', size: 'medium' }}
                        text={t('Criar')}
                      />
                    </div>
                  ) : (
                    <Button
                      onClick={() => setShowCreateForm(true)}
                      theme={{ type: 'secondary', size: 'medium' }}
                      text={t('Nova lista')}
                    />
                  )}

                  <Button
                    onClick={handleCloseModal}
                    theme={{ type: 'primaryLivros', size: 'medium' }}
                    text={t('Concluir')}
                  />
                </>
              )}
            </div>
          </div>,
          portalContainer
        )}
    </>
  )
}

export default ButtonAddToList
